import React, { useState } from 'react';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import { DownloadSheet } from '../../../assets/svg/icons';
import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import DeliveredRxReasonChangeReviewTable, {
	DELIVERED_RX_REASON_SNOOZE_OPTIONS,
	getAppTimeLocalValue,
	useDeliveredRxReasonChangeReviews,
} from './DeliveredRxReasonChangeReviewShared';

const DeliveredRxReasonChangeReviewModal = ({
	records,
	totalCount,
	reasons,
	reasonOptions,
	errors,
	submitting,
	exporting,
	onReasonChange,
	onExportCsv,
	onSubmit,
	onSkip,
}) => {
	const [snoozeOption, setSnoozeOption] = useState(DELIVERED_RX_REASON_SNOOZE_OPTIONS[0].value);
	const [customSnoozeTime, setCustomSnoozeTime] = useState(() =>
		getAppTimeLocalValue(new Date(Date.now() + 60 * 60 * 1000))
	);
	const [isSkipMenuOpen, setIsSkipMenuOpen] = useState(false);

	if (!records.length) return null;

	const isCustomSnooze = snoozeOption === 'custom';
	const isSkipDisabled = submitting || exporting || (isCustomSnooze && !customSnoozeTime);
	const handleSkipClick = (snoozeConfig) => {
		onSkip({
			minutes: snoozeConfig?.minutes,
			customTime: snoozeConfig?.customTime,
		});
		setIsSkipMenuOpen(false);
	};

	return (
		<div className="fixed inset-0 z-[172] flex items-center justify-center bg-black/50 px-4 py-6">
			<div className="w-[92vw] max-w-[1540px] max-h-[90vh] overflow-visible rounded-xl border border-gray-200 bg-white shadow-xl">
				<div className="border-b border-gray-200 bg-gradient-to-r from-gray-100 to-gray-100 px-5 py-4">
					<div className="flex flex-wrap items-center justify-between gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
								<ReportProblemOutlinedIcon fontSize="small" />
							</span>
							<h2 className="text-lg font-semibold text-gray-900">Delivered RX Reason Change Review</h2>
							{totalCount > 1 && (
								<span className="rounded-md bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">
									{totalCount} pending
								</span>
							)}
						</div>
						<button
							type="button"
							onClick={onExportCsv}
							disabled={submitting || exporting}
							className={`flex h-[38px] items-center justify-center gap-2 rounded px-4 text-sm font-medium text-white transition ${
								submitting || exporting
									? 'cursor-not-allowed bg-red-300'
									: 'bg-red-500 hover:bg-red-600'
							}`}
						>
							<DownloadSheet />
							{exporting ? 'Exporting...' : 'Export CSV'}
						</button>
					</div>
				</div>

				<div className="max-h-[62vh] overflow-auto">
					<DeliveredRxReasonChangeReviewTable
						records={records}
						reasons={reasons}
						reasonOptions={reasonOptions}
						errors={errors}
						submitting={submitting}
						onReasonChange={onReasonChange}
					/>
				</div>

				<div className="relative z-30 flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 px-5 py-4">
					<div className="relative">
						<button
							type="button"
							onClick={() => setIsSkipMenuOpen((isOpen) => !isOpen)}
							disabled={submitting || exporting}
							className={`relative z-10 border px-5 py-2 text-sm font-semibold transition ${
								submitting || exporting
									? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
									: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
							} ${isSkipMenuOpen ? 'rounded-b-md rounded-t-none border-t-white' : 'rounded-md'}`}
						>
							Skip
						</button>
						{isSkipMenuOpen && (
							<div className="absolute bottom-full right-0 z-50 -mb-px w-52 overflow-hidden rounded-md rounded-br-none border border-gray-300 bg-white shadow-lg">
								{DELIVERED_RX_REASON_SNOOZE_OPTIONS.filter((option) => option.value !== 'custom').map((option) => (
									<button
										key={option.value}
										type="button"
										onClick={() => handleSkipClick({ minutes: option.minutes })}
										className="block w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
									>
										{option.label}
									</button>
								))}
								<div className="border-t border-gray-200 px-3 py-2">
									<button
										type="button"
										onClick={() => setSnoozeOption('custom')}
										className={`${snoozeOption === 'custom' ? 'mb-2' : ''} block w-full text-left text-sm font-semibold text-gray-700`}
									>
										Custom
									</button>
									{snoozeOption === 'custom' && (
										<div className="flex items-center gap-2">
											<input
												type="time"
												value={customSnoozeTime}
												onChange={(event) => setCustomSnoozeTime(event.target.value)}
												className="h-9 min-w-0 flex-1 rounded-md border border-gray-300 px-2 text-sm font-medium text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
											/>
											<button
												type="button"
												onClick={() => handleSkipClick({ customTime: customSnoozeTime })}
												disabled={isSkipDisabled}
												className={`h-9 rounded-md px-3 text-sm font-semibold transition ${
													isSkipDisabled
														? 'cursor-not-allowed bg-slate-100 text-slate-500'
														: 'bg-blue-600 text-white hover:bg-blue-700'
												}`}
											>
												Set
											</button>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={onSubmit}
						disabled={submitting || exporting}
						className={`rounded-md px-5 py-2 text-sm font-semibold transition ${
							submitting || exporting
								? 'cursor-not-allowed bg-slate-200 text-slate-600'
								: 'bg-blue-600 text-white hover:bg-blue-700'
						}`}
					>
						{submitting ? 'Saving...' : 'Continue'}
					</button>
				</div>
			</div>
		</div>
	);
};

const DeliveredRxReasonChangeReviewPopupManager = () => {
	
	const { pharmacyId, profileData, teamStatus } = useCustomSelectors();
	const isPharmacist = String(profileData?.type || '').toLowerCase() === 'pharmacist';
	const isTeamUserRestricted = process.env.NODE_ENV === 'production' && Number(teamStatus) === 1;
	// This manager is session-level: it checks once after login/reload and does
	// not depend on the current route, so page navigation cannot reload it.
	const isEnabled = Boolean(pharmacyId && profileData?.user_id && isPharmacist && !isTeamUserRestricted);
	
	const {
		records,
		reasons,
		reasonOptions,
		fieldErrors,
		submitting,
		exporting,
		handleReasonChange,
		handleExportCsv,
		handleSubmit,
		handleSkip,
	} = useDeliveredRxReasonChangeReviews({
		enabled: isEnabled,
		enableSnooze: true,
		pharmacyId,
		userId: profileData?.user_id,
	});

	if (!isEnabled) {
		return null;
	}

	return (
		<DeliveredRxReasonChangeReviewModal
			records={records}
			totalCount={records.length}
			reasons={reasons}
			reasonOptions={reasonOptions}
			errors={fieldErrors}
			submitting={submitting}
			exporting={exporting}
			onReasonChange={handleReasonChange}
			onExportCsv={handleExportCsv}
			onSubmit={handleSubmit}
			onSkip={handleSkip}
		/>
	);
};

export default DeliveredRxReasonChangeReviewPopupManager;
